import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';

type TopupCreditsModalContextValue = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
};

const noop = () => undefined;

// Outside a provider the opener does nothing rather than throwing, so a
// component that offers "Buy credits" can render anywhere.
const TopupCreditsModalContext = createContext<TopupCreditsModalContextValue>({
  isOpen: false,
  open: noop,
  close: noop
});

/**
 * Holds the open state of the shell-level Top up Credits modal, so anything
 * under the app shell (e.g. a promo banner) can open it without hosting its
 * own copy. The modal itself is rendered by `TopupCreditsModalHost`, which
 * must sit inside `CreditsProvider`.
 */
export function TopupCreditsModalProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const value = useMemo(() => ({ isOpen, open, close }), [isOpen, open, close]);

  return (
    <TopupCreditsModalContext.Provider value={value}>
      {children}
    </TopupCreditsModalContext.Provider>
  );
}

export function useTopupCreditsModal() {
  return useContext(TopupCreditsModalContext);
}
